/* eslint-disable react/prop-types */
import { Map } from 'ol';
import {
  createContext, ReactNode, RefObject, useContext, useMemo, forwardRef, useImperativeHandle,
} from 'react';
import { Product } from 'types/app.types';
import useStableValue from 'utils/hooks/useStableValue';
import MainAppContext from '../MainAppContext';
import useMap from './hooks/useMap';

// Interface for imperative methods exposed via ref
export interface MapViewContextRef {
  preloadAllLayers: (timestampList: string[]) => Promise<void>;
}

interface ContextValues {
  map?: Map | null,
  mapContainer?: RefObject<HTMLDivElement>,
  popupContainer?: RefObject<HTMLDivElement>,
  loadingLayer: boolean,
  loadingAnimationCharge: number | null,
  popupInfo: Record<string, unknown>,
  index: number,
  updateLayerStyle: (productIndex: number, styleName: string, filterIds?: string[]) => void,
  updateLayerOpacity: (productIndex: number, opacity: number) => void,
  updateLayerElevation: (productIndex: number, opacity: number) => void,
  updateGeoLayer: () => void,
  currentSourceProjection?: string,
  addLayer: (product: Product, time: string) => void,
  removeLayer: (product: Product, removeWholeProduct?: boolean) => void,
  prechargeAnimation: () => Promise<void>,
  cancelPrechargeAnimation: () => void,
  moveLayer: (oldIndex: number, newIndex: number) => void,
  replaceLayer: (product: Product) => void,
  preloadLayer: (args: {
    index: number, product: Product, generatedTimestampList: string[] }) => Promise<void>
  preloadAllLayers: (timestampList: string[]) => Promise<void>,
}

interface MapViewProviderProps {
  children: ReactNode,
  index: number
}

const MapViewContext = createContext<ContextValues>({
  map: undefined,
  mapContainer: undefined,
  loadingLayer: false,
  loadingAnimationCharge: null,
  popupContainer: undefined,
  popupInfo: {},
  index: 0,
  updateLayerStyle: () => {},
  updateLayerOpacity: () => {},
  updateLayerElevation: () => {},
  updateGeoLayer: () => {},
  currentSourceProjection: undefined,
  addLayer: () => {},
  removeLayer: () => {},
  prechargeAnimation: () => new Promise<void>((resolve) => { resolve(); }),
  cancelPrechargeAnimation: () => {},
  moveLayer: () => {},
  replaceLayer: () => {},
  preloadLayer: async () => Promise.resolve(),
  preloadAllLayers: async () => Promise.resolve(),
});

const MapViewProvider = forwardRef<MapViewContextRef, MapViewProviderProps>(
  ({ children, index }, ref) => {
    const { viewMode } = useContext(MainAppContext);
    // useMap returns a new object (with new function identities) on every render, so memoizing
    // it directly never worked and all consumers re-rendered with every provider render.
    const props = useStableValue(useMap(index, viewMode));

    // Expose imperative methods via ref
    useImperativeHandle(ref, () => ({
      preloadAllLayers: props.preloadAllLayers,
    }), [props.preloadAllLayers]);

    const value = useMemo<ContextValues>(
      () => ({
        ...props,
        index,
      }),
      [props, index],
    );

    return (
      <MapViewContext.Provider value={value}>
        {children}
      </MapViewContext.Provider>
    );
  },
);

export { MapViewProvider };
export default MapViewContext;
